// src/components/ui/button.tsx
import * as React from "react";
import clsx from "clsx";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "destructive"
  | "accent"
  | "success"
  | "warning"
  | "info"
  | "link"
  | "tab";

export type ButtonSize = "sm" | "md" | "lg" | "icon";

/**
 * The semantic family a button speaks in, independent of how loud it is.
 *
 * Tone and loudness are two different questions — "is this destructive?" and
 * "is this the main action here?" — and the pages were already answering them
 * separately, writing `variant="ghost"` for the loudness and
 * `className="text-danger-500"` for the meaning. The intent was right; only
 * the channel was wrong, because a className colour loses to the variant's own
 * colour on stylesheet order and never renders.
 *
 * So the second question gets a real prop. 123 buttons across 28 files wanted
 * this, in 3 loudness levels × 6 families. Expressing that as variant names
 * would mean 18 more entries in the union — `ghost-danger`, `outline-danger`,
 * `link-danger` … — which is the same two questions multiplied out instead of
 * kept apart.
 */
export type ButtonTone =
  | "danger"
  | "success"
  | "warning"
  | "info"
  | "accent"
  | "secondary";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** موجود للتوافق مع واجهات أخرى، لكنه غير مستخدم هنا */
  asChild?: boolean;
  variant?: ButtonVariant;
  /**
   * اللون الدلالي: أحمر للحذف، أخضر للتأكيد… يُركَّب مع `variant` ولا يحلّ محلّه.
   * `variant` يحدّد العلوّ (صلب/محدَّد/هادئ)، و`tone` يحدّد المعنى.
   */
  tone?: ButtonTone;
  size?: ButtonSize;
  /** يعطل الزر ويعرض مؤشّر تحميل */
  isLoading?: boolean;
  /** أيقونة قبل النص */
  leftIcon?: React.ReactNode;
  /** أيقونة بعد النص */
  rightIcon?: React.ReactNode;
  /** تمدّد بعرض الحاوية */
  fullWidth?: boolean;
  /**
   * زرّ لا يتغيّر لونه عند المرور: مؤشّر حالة أو وجهة، لا فعل يستجيب.
   *
   * ست مواضع في ثلاثة ملفات كانت تكتب هذا بيدها:
   * `transition-none hover:!bg-th-primary hover:!text-th-surface hover:opacity-100`
   * — وهو تعريف الخاصّية الناقصة. وكانت تلك الكتابة **لا تفي بما تعد**:
   * `bg-th-primary` يطابق `bg-accent-700` في كلّ سمة (قيس 1.00:1 بلا مدى)،
   * فالتعبئة تتجمّد فعلًا؛ أمّا `text-th-surface` فيختلف عن
   * `text-accent-foreground` (المدى يبلغ 1.30:1)، فالنصّ **كان يقفز** إلى لون
   * لم يُختر لقراءته فوق هذه التعبئة — 5.75:1 بدل 6.76:1.
   *
   * هنا لا يُذكر أيّ لون: تُحذف أصناف `hover:` من وصفة النمط نفسها، فيبقى
   * لون السكون كما هو بالضبط. يعمل مع كلّ `variant` وكلّ `tone` دون تعديل،
   * ولا يمكن أن ينحرف عنهما لأنّه مشتقّ منهما لا مكتوب بجوارهما.
   */
  staticHover?: boolean;
}

/**
 * Solid variants all follow one recipe: `bg-<family>-700` for the fill,
 * `text-<family>-foreground` for the label, `hover:bg-<family>-800` for the
 * hover. It is not a style preference — it is the only combination the theme
 * can guarantee.
 *
 * `-foreground` is derived against `-700`, so pairing it with the raw `-500`
 * fill (which is what `bg-[rgb(var(--color-success))]` did) breaks the
 * guarantee: measured across the presets that pairing reaches 3.30:1 for
 * success and 2.94:1 for warning.
 *
 * Three variants were worse than that. They named classes that do not exist —
 * `text-th-danger-foreground`, where the config declares `danger-foreground`
 * without the `th-` prefix — so Tailwind emitted nothing and the label simply
 * inherited the page's body text onto a coloured fill: 2.12:1 for destructive,
 * 1.46:1 for warning. A misspelt colour class fails silently, which is why
 * these are measured in `scripts/check-button-variants.js` rather than trusted.
 *
 * `primary` and `accent` moved to the same recipe for a different reason. They
 * used `text-th-surface` — near-white — on the admin's raw brand colour. That
 * passes with today's five presets and is pure luck: Settings lets an admin
 * choose any primary, and a pale one would put white on white. `-700` is the
 * same colour when it is already dark enough, and a corrected one when it is
 * not.
 */
function variantClasses(variant: ButtonVariant) {
  switch (variant) {
    case "secondary":
      return "bg-th-surface-alt text-th-text hover:bg-th-border";
    case "outline":
      // The fill matches the card behind it, so the border is the only thing
      // marking where the button is: it needs the boundary colour, not the
      // divider one.
      return "border border-th-border-strong bg-th-surface text-th-text hover:bg-th-surface-alt";
    case "ghost":
      return "bg-transparent text-th-text hover:bg-th-surface-alt";
    case "destructive":
      return "bg-danger-700 text-danger-foreground hover:bg-danger-800";
    case "accent":
      return "bg-secondary-700 text-secondary-foreground hover:bg-secondary-800";
    case "success":
      return "bg-success-700 text-success-foreground hover:bg-success-800";
    case "warning":
      return "bg-warning-700 text-warning-foreground hover:bg-warning-800";
    // the information's solid, as the three above (2026-11-02 — the displays' «معلومة» sent in the theme's green)
    case "info":
      return "bg-info-700 text-info-foreground hover:bg-info-800";
    case "link":
      return "bg-transparent p-0 h-auto text-th-primary underline-offset-4 hover:underline";
    // A tab in its unselected state. It sets the edge COLOUR only, never the
    // edge width: the strip that owns the tab decides whether the marker is
    // `border-b-2`, `border-s-4`, or something else, and that is layout, not
    // theme. Twelve tab sites were found spelling this by hand in three
    // different ways -- `border-accent-600`, `border-th-primary`, and a third
    // -- which is what a missing variant looks like from the outside.
    //
    // No rounding or height is forced. A tab has no fill, so the base
    // `rounded-md` is invisible on it; overriding it would mean shipping
    // `rounded-none` and hoping it sorts after `rounded-md`, which is a
    // coin-flip on stylesheet order rather than a decision.
    case "tab":
      return "bg-transparent border-transparent text-th-text-muted hover:text-th-text";
    case "primary":
    default:
      return "bg-accent-700 text-accent-foreground hover:bg-accent-800";
  }
}

/**
 * The same button, spoken in a semantic family.
 *
 * Every pairing below is a row of the agreed contract in `theme-rules.js`,
 * already measured across all five presets in both modes. Nothing here is a
 * new colour decision; it is the existing decisions, assembled:
 *
 *     text-X-700   on bg-th-surface   5.15:1   "نصّ حالة على بطاقة"
 *     text-X-700   on bg-X-100        4.52:1   "شارة حالة"
 *     text-X-700   on bg-X-50         4.83:1   "شارة حالة فاتحة"
 *     border-X-700 on bg-th-surface   5.15:1   "حدّ ملوّن دالّ على بطاقة"
 *
 * `-700` does the work in all four. That is not a coincidence: it is the first
 * step on the scale that clears the bar at all. The rungs below it were
 * measured and top out at 1.48:1 — which is why the pages that reached for
 * `text-danger-500` were asking for something the theme cannot deliver, quite
 * apart from the cascade swallowing it.
 *
 * WHY THIS IS A TABLE AND NOT A TEMPLATE
 * --------------------------------------
 * The obvious way to write this is `text-${family}-700`. It does not work, and
 * it fails silently, which is worse: Tailwind finds classes by scanning source
 * text for literal strings, so an interpolated name is never generated and the
 * element ends up with no colour at all. Verified rather than assumed —
 * `hover:bg-info-100` was absent from the built stylesheet until it was spelt
 * out here.
 *
 * That is the same failure this codebase already paid for once, when 161
 * misspelt `th-*` classes emitted nothing and silently inherited body text.
 * A table is repetitive; a template is a phantom-class generator.
 *
 * `solid` is deliberately absent. A solid coloured button is what the existing
 * `destructive` / `success` / `warning` variants already are, and adding a
 * second spelling for a thing that already has one is how a system grows two
 * sources of truth.
 *
 * WHY `soft` CARRIES A BORDER AND `outline` DOES NOT
 * --------------------------------------------------
 * They answer different questions. `outline` is an ACTION that happens to be
 * dangerous or successful -- "حذف الكل" on a toolbar -- and it sits on the page
 * with a transparent fill so the card shows through. `soft` is a STATE: the
 * chosen card in a row of choices, which the pages spell out twenty times over
 * as border + tinted fill + 700 text.
 *
 * Dropping the border from `soft` would have meant every one of those sites
 * kept a `border-danger-500` in its className -- a semantic colour outside the
 * theme, which is the thing this whole exercise exists to remove. So the
 * border belongs in the recipe.
 *
 * It is the same `-700` rung the outline shape uses, which makes `soft` simply
 * "outline, filled". The first attempt used `-200` on the reasoning that a
 * filled chip already marks its own boundary, so the edge could be decorative.
 * The measurement disagreed -- 1.13:1 to 1.64:1, six combinations under the
 * bar -- and the honest response was to move the rung, not to write the
 * exception into this comment. A rule that only holds in prose is one the
 * audit cannot keep, and the next person to add a tone would have inherited a
 * checker that fails on arrival.
 *
 * A strong ring on a selected chip is right anyway: it has to separate itself
 * from the unselected chips beside it.
 */
type ToneShape = "ghost" | "outline" | "soft" | "link" | "tab";

const TONE_RECIPES: Record<ButtonTone, Record<ToneShape, string>> = {
  danger: {
    ghost: "bg-transparent text-danger-700 hover:bg-danger-100",
    outline: "border border-danger-700 bg-transparent text-danger-700 hover:bg-danger-100",
    soft: "border border-danger-700 bg-danger-50 text-danger-700 hover:bg-danger-100",
    link: "bg-transparent p-0 h-auto text-danger-700 underline-offset-4 hover:underline",
    tab: "bg-transparent border-danger-700 text-danger-700",
  },
  success: {
    ghost: "bg-transparent text-success-700 hover:bg-success-100",
    outline: "border border-success-700 bg-transparent text-success-700 hover:bg-success-100",
    soft: "border border-success-700 bg-success-50 text-success-700 hover:bg-success-100",
    link: "bg-transparent p-0 h-auto text-success-700 underline-offset-4 hover:underline",
    tab: "bg-transparent border-success-700 text-success-700",
  },
  warning: {
    ghost: "bg-transparent text-warning-700 hover:bg-warning-100",
    outline: "border border-warning-700 bg-transparent text-warning-700 hover:bg-warning-100",
    soft: "border border-warning-700 bg-warning-50 text-warning-700 hover:bg-warning-100",
    link: "bg-transparent p-0 h-auto text-warning-700 underline-offset-4 hover:underline",
    tab: "bg-transparent border-warning-700 text-warning-700",
  },
  info: {
    ghost: "bg-transparent text-info-700 hover:bg-info-100",
    outline: "border border-info-700 bg-transparent text-info-700 hover:bg-info-100",
    soft: "border border-info-700 bg-info-50 text-info-700 hover:bg-info-100",
    link: "bg-transparent p-0 h-auto text-info-700 underline-offset-4 hover:underline",
    tab: "bg-transparent border-info-700 text-info-700",
  },
  accent: {
    ghost: "bg-transparent text-accent-700 hover:bg-accent-100",
    outline: "border border-accent-700 bg-transparent text-accent-700 hover:bg-accent-100",
    soft: "border border-accent-700 bg-accent-50 text-accent-700 hover:bg-accent-100",
    link: "bg-transparent p-0 h-auto text-accent-700 underline-offset-4 hover:underline",
    tab: "bg-transparent border-accent-700 text-accent-700",
  },
  secondary: {
    ghost: "bg-transparent text-secondary-700 hover:bg-secondary-100",
    outline: "border border-secondary-700 bg-transparent text-secondary-700 hover:bg-secondary-100",
    soft: "border border-secondary-700 bg-secondary-50 text-secondary-700 hover:bg-secondary-100",
    link: "bg-transparent p-0 h-auto text-secondary-700 underline-offset-4 hover:underline",
    tab: "bg-transparent border-secondary-700 text-secondary-700",
  },
};

function toneClasses(variant: ButtonVariant, tone: ButtonTone) {
  switch (variant) {
    case "ghost":
      return TONE_RECIPES[tone].ghost;
    case "outline":
      return TONE_RECIPES[tone].outline;
    case "secondary":
      return TONE_RECIPES[tone].soft;
    case "link":
      return TONE_RECIPES[tone].link;
    case "tab":
      return TONE_RECIPES[tone].tab;
    // A tone on a solid variant would fight the variant's own family, and one
    // of the two has to lose. The variant wins, and says so here rather than
    // silently.
    default:
      return variantClasses(variant);
  }
}

function sizeClasses(size: ButtonSize) {
  switch (size) {
    case "sm":
      return "h-8 px-3 text-sm";
    case "lg":
      return "h-12 px-5 text-base";
    case "icon":
      return "p-0 [width:var(--icon-size)] [height:var(--icon-size)] [border-radius:var(--icon-radius)]";
    case "md":
    default:
      return "h-10 px-4 text-sm";
  }
}

/**
 * The button recipe as a string, for the few places that must not be a
 * `<button>`.
 *
 * Navigation that looks like a button has to stay an `<a>`: middle-click,
 * "open in new tab" and the browser's own link affordances are not things a
 * click handler can imitate. So two `<Link>`s in `LiveHeader` had copied the
 * whole recipe inline — geometry variables, fill, label, border, and a hover
 * freeze — which is a second place to change every time the first one changes.
 *
 * They had already diverged: both still forced `bg-th-primary` on hover while
 * the variant had moved to `bg-accent-700`. The two happen to be the same
 * colour today (measured 1.00:1 with no spread across every preset), so the
 * copy looked correct — it was right by luck, not by construction, and the
 * matching `text-th-surface` was NOT the same as `text-accent-foreground`.
 *
 * Exporting the recipe costs one function and removes the second copy.
 *
 * `mc-theme-ui`'s injected rules (ThemeProvider.tsx) key off `[data-variant]`
 * on `button` and `a` elements to apply the admin's `--btn-radius`/`--btn-px`/
 * `--btn-py`/`--btn-fs`. This function only returns a className string — it
 * cannot attach attributes — so every caller MUST also set
 * `data-variant={variant}` and `data-size={size}` on the element by hand, or
 * the element keeps Tailwind's static geometry while its siblings (real
 * `<Button>`s, which set these props themselves) pick up the theme's. That
 * mismatch is exactly what made the `/live` nav links look a different size
 * from the stats button next to them.
 */
export function buttonClasses({
  variant = "primary",
  tone,
  size = "md",
  fullWidth = false,
  staticHover = false,
  className,
}: {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  fullWidth?: boolean;
  staticHover?: boolean;
  className?: string;
} = {}) {
  /* The resting recipe with its hover states removed.
     Deriving it by filtering is what makes `staticHover` safe: there is no
     second list of colours to keep in step, so a new variant or tone inherits
     the behaviour the day it is added. Writing the frozen colours out by hand
     is exactly what the pages were doing, and it had already drifted. */
  const palette = tone ? toneClasses(variant, tone) : variantClasses(variant);
  const resting = staticHover
    ? palette
        .split(/\s+/)
        .filter((cls) => cls && !cls.startsWith("hover:"))
        .join(" ")
    : palette;

  return clsx(
    // أساسيات الشكل وإمكانية الوصول
    "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors whitespace-nowrap",
    "focus:outline-none focus:ring-2 focus:ring-th-primary/40 focus:ring-offset-2",
    "disabled:opacity-50 disabled:pointer-events-none select-none",
    // الحجم/النمط
    sizeClasses(size),
    resting,
    staticHover && "transition-none",
    // الحواف/الأبعاد الإضافية
    fullWidth && "w-full",
    className
  );
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      tone,
      size = "md",
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      staticHover = false,
      disabled,
      title,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    const button = (
      <button
        ref={ref}
        data-variant={variant}
        data-tone={tone}
        data-size={size}
        aria-busy={isLoading || undefined}
        aria-disabled={isDisabled || undefined}
        disabled={isDisabled}
        title={title}
        className={buttonClasses({ variant, tone, size, fullWidth, staticHover, className })}
        {...props}
      >
        {/* مؤشر التحميل */}
        {isLoading && (
          <svg
            className="animate-spin"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              d="M4 12a8 8 0 018-8"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        )}

        {!isLoading && leftIcon ? <span className="shrink-0">{leftIcon}</span> : null}

        {children}

        {!isLoading && rightIcon ? <span className="shrink-0">{rightIcon}</span> : null}
      </button>
    );

    /**
     * الزرّ المعطَّل لا يُظهر تلميحه.
     *
     * Chrome وSafari لا تُرسل أحداث الفأرة إلى عنصر نموذج معطَّل، فالـ`title`
     * عليه لا يظهر أبدًا — وهو بالضبط الحال الذي يحتاج المستخدم فيه التفسير
     * («لماذا لا أستطيع الضغط؟»). الحلّ: غلاف غير معطَّل يحمل التلميح،
     * فيستقبل هو حدث المرور. يبقى التلميح على الزرّ أيضًا لمتصفّح يعرضه.
     * الغلاف `inline-flex` كي لا يغيّر التخطيط داخل الصفوف ذات الفجوات.
     */
    if (isDisabled && title) {
      return (
        <span title={title} className={fullWidth ? "inline-flex w-full" : "inline-flex"}>
          {button}
        </span>
      );
    }
    return button;
  }
);
Button.displayName = "Button";
